/**
 * @file useCustomDialog.js
 * @description ダイアログコンポーネントの状態管理と制御を行うカスタムフック
 *
 * このモジュールは、Material-UIのダイアログコンポーネントを
 * 簡単に制御するためのカスタムフックを提供します。
 *
 * 主な機能：
 * - ダイアログの表示/非表示の制御
 * - ダイアログのパラメータ管理
 * - ダイアログコンポーネントの動的レンダリング
 *
 * @example
 * // 基本的な使用方法
 * const [dialogState, dialogProps] = useCustomDialog(MyDialogComponent);
 *
 * // ダイアログを開く
 * dialogProps.handleOpen({ title: '確認', message: '処理を実行しますか？' });
 *
 * // コンポーネント内での使用
 * return (
 *   <>
 *     <Button onClick={() => dialogProps.handleOpen()}>ダイアログを開く</Button>
 *     {dialogProps.renderDialog()}
 *   </>
 * );
 *
 * @author MetroJS Team
 * @version 1.0.0
 */

"use client";
import React from "react";
import { useState, useCallback, useMemo, useRef, useId } from "react";
import { Dialog, DialogTitle, DialogContent, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import logjs from "@metrojs/logjs";
const log = new logjs("hooks/useCustomDialog");

/**
 * ダイアログコンポーネントの状態管理と制御を行うカスタムフック
 *
 * @param {React.ComponentType} DialogComponent - ダイアログコンポーネント
 * @param {Object} [options] - オプション
 * @param {boolean} [options.resetOnClose=false] - true の場合、閉じるたびに key を更新し次回表示時にアンマウント→再マウントして初期状態にする
 * @returns {Array} [dialogState, dialogProps] - ダイアログの状態と制御関数
 *
 * @example
 * const [dialogState, dialogProps] = useCustomDialog(MyDialogComponent);
 *
 * // 閉じるたびに初期状態で開き直す場合
 * const [dialogState, dialogProps] = useCustomDialog(MyDialogComponent, { resetOnClose: true });
 *
 * // ダイアログを開く
 * dialogProps.handleOpen({ title: '確認', message: '処理を実行しますか？' });
 *
 * // ダイアログを閉じる
 * dialogProps.handleClose();
 *
 * // ダイアログの表示状態を確認
 * if (dialogProps.isOpen) {
 *   // ダイアログが開いている場合の処理
 * }
 */
const useCustomDialog = (DialogComponent, options = {}) => {
  const { resetOnClose = false } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [params, setParams] = useState({});
  // 閉じたあと再マウント用（resetOnClose 時のみ意味を持つ）
  const [dialogKey, setDialogKey] = useState(0);
  const instanceId = useId();
  const onCloseCallbackRef = useRef(null);

  const open = (_params = {}, _onClose = null) => {
    setParams(_params);
    onCloseCallbackRef.current = _onClose; // refに直接設定
    setIsOpen(true);
    log.debug("handleOpen", _params);
  };

  const onClose = useCallback((_result = {}) => {
    setIsOpen(false);
    log.debug("handleClose", _result);

    // 個別のonCloseコールバックが存在する場合は実行
    if (onCloseCallbackRef.current) {
      log.debug("onCloseCallback found");
      onCloseCallbackRef.current(_result);
      onCloseCallbackRef.current = null; // 実行後にクリア
    } else {
      log.debug("onCloseCallback not found");
    }

    return _result;
  }, []);

  const wrappedOnClose = useCallback(
    (result) => {
      setDialogKey((k) => k + 1);
      onClose(result);
    },
    [onClose]
  );

  const dialogProps = useMemo(
    () => ({
      open: isOpen,
      onClose: resetOnClose ? wrappedOnClose : onClose,
      params,
    }),
    [isOpen, params, onClose, wrappedOnClose, resetOnClose]
  );

  const renderDialog = useCallback(() => {
    if (!DialogComponent) {
      return null;
    }
    try {
      if (resetOnClose) {
        return React.createElement(
          React.Fragment,
          { key: `dialog-${instanceId}-${dialogKey}` },
          React.createElement(DialogComponent, dialogProps)
        );
      }
      return React.createElement(DialogComponent, dialogProps);
    } catch (error) {
      log.error("Error rendering dialog:", error);
      return null;
    }
  }, [DialogComponent, dialogProps, resetOnClose, dialogKey, instanceId]);

  /**
   * ダイアログコンポーネントをレンダリングする（title/content を返すコンポーネント用）
   * フックの順序を保つため、isOpen に関係なく常に DialogComponent を実行する。
   * @returns {React.ReactNode} ダイアログコンポーネント
   */
  const renderDialogEx = () => {
    if (!DialogComponent) {
      return null;
    }
    let title,
      content,
      isModal = false,
      size = "md",
      fullWidth = true;
    let restParams = {};
    try {
      // 常に実行してフックの順序を一定に保つ（Rules of Hooks）
      const config = typeof DialogComponent === "function" ? DialogComponent(dialogProps) : null;
      if (!isOpen) {
        return null;
      }
      if (config && typeof config === "object" && "content" in config) {
        title = config.title;
        content = config.content;
        isModal = config.isModal ?? false;
        const params = config.params && typeof config.params === "object" ? config.params : {};
        const rawSize = params.size ?? "md";
        const rawFullWidth = params.fullWidth ?? true;
        size = typeof rawSize === "string" && ["xs", "sm", "md", "lg", "xl"].includes(rawSize) ? rawSize : "md";
        fullWidth = Boolean(rawFullWidth);
        const { size: _s, fullWidth: _f, ...dialogRest } = params;
        restParams = typeof dialogRest === "object" && dialogRest !== null ? dialogRest : {};
      } else {
        return null;
      }
    } catch (err) {
      const msg = err?.message ?? String(err);
      const stack = err?.stack;
      log.error("Error getting dialog config:", msg, stack ?? "");
      return null;
    }
    try {
      return (
        <Dialog
          open={isOpen}
          onClose={onClose}
          maxWidth={size}
          fullWidth={fullWidth}
          {...restParams}
          hideBackdrop={false}
          style={!isModal ? { pointerEvents: "none" } : { pointerEvents: "auto" }}
          BackdropProps={
            isModal
              ? {
                  // 背面を薄く暗くして操作をブロック
                  sx: { backgroundColor: "rgba(0, 0, 0, 0.4)" },
                }
              : {
                  style: { pointerEvents: "none" },
                }
          }
          PaperProps={
            !isModal
              ? {
                  style: { pointerEvents: "auto" },
                }
              : undefined
          }
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            <IconButton onClick={onClose} size="small" aria-label="閉じる">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>{content}</DialogContent>
        </Dialog>
      );
    } catch (err) {
      const msg = err?.message ?? String(err);
      log.error("Error rendering dialog JSX:", msg, err?.stack ?? "");
      return null;
    }
  };

  return {
    open,
    onClose,
    isOpen,
    params,
    dialogProps,
    renderDialog,
    renderDialogEx,
  };
};

export default useCustomDialog;
